// ============================================================================
// smartLaunchContinuationGate.js — SMART Build 2L (pure; client continuation logic)
//
// Pure helpers for the SMART Launch Continue panel. NO React, NO Firestore, NO
// browser APIs beyond URLSearchParams (which is also available in Node for the
// verifier). Decides:
//   - launchSessionId format validity + URL parsing,
//   - whether the signed-in role may continue (mirrors server validateGpRoleActive),
//   - safe status lines + CTA state from the read-only continuation projection,
//   - typed-error-code → safe UI copy (never a stack trace).
//
// The panel (SmartLaunchContinueView) is the only consumer; the server callables
// remain the authority. This module never touches token/patient/fhirUser/PHI.
// ============================================================================

// Must match functions/fhir/smartAuthorization.js SESSION_ID_RE.
export const LAUNCH_SESSION_ID_RE = /^ehrls_[A-Za-z0-9-]{8,}$/;

/** True when value is a well-formed launch session id. */
export function isValidLaunchSessionId(value) {
  return typeof value === 'string' && LAUNCH_SESSION_ID_RE.test(value);
}

/**
 * Parse a validated launchSessionId from a URL query string. Returns null when
 * absent or malformed (defensive — never throws).
 * @param {string} search  e.g. window.location.search ("?launchSessionId=ehrls_…")
 */
export function parseLaunchSessionIdFromSearch(search) {
  try {
    const params = new URLSearchParams(typeof search === 'string' ? search : '');
    const id = params.get('launchSessionId');
    return isValidLaunchSessionId(id) ? id : null;
  } catch {
    return null;
  }
}

// next-action hints — mirror functions/fhir/smartLaunchContinuation.js.
export const CONTINUATION_NEXT_ACTION = Object.freeze({
  CREATE_AND_IMPORT: 'CREATE_AND_IMPORT',
  IMPORT: 'IMPORT',
  OPEN_PACKET_INBOX: 'OPEN_PACKET_INBOX',
  RELAUNCH: 'RELAUNCH',
  BLOCKED: 'BLOCKED',
});

/**
 * Role gate — mirrors the server validateGpRoleActive (gp_employee + active +
 * tenant). The panel renders the precise blocked copy itself (it is mounted
 * role-agnostically, not behind the generic AccessDenied guard).
 * @param {{ role?: string, isActive?: boolean, tenantId?: string|null }} profile
 * @returns {{ allowed: boolean, reason: string|null, message: string|null }}
 */
export function getContinuationRoleState({ role, isActive, tenantId } = {}) {
  if (!role) return { allowed: false, reason: 'UNAUTHENTICATED', message: 'Sign in to ReferralGate to continue this EHR launch.' };
  if (role !== 'gp_employee') return { allowed: false, reason: 'NOT_GP', message: 'Only a referring provider can continue this EHR launch.' };
  if (isActive !== true) return { allowed: false, reason: 'INACTIVE', message: 'Your account is not active. Contact your administrator.' };
  if (!tenantId) return { allowed: false, reason: 'NO_TENANT', message: 'Your account is not assigned to a clinic yet.' };
  return { allowed: true, reason: null, message: null };
}

/**
 * Safe status checklist from the read-only projection. No PHI — presence/status
 * booleans only.
 * @returns {Array<{ label: string, done: boolean }>}
 */
export function getContinuationStatusLines(projection) {
  const p = projection || {};
  const ctxPresent = p.contextStatus === 'CONTEXT_PRESENT';
  const patient = p.patientContextPresent === true;
  const ready = p.canCreatePacket === true || p.canImport === true
    || !!p.linkedPacketId || p.nextAction === CONTINUATION_NEXT_ACTION.OPEN_PACKET_INBOX;
  return [
    { label: 'EHR authorization complete', done: !!projection },
    { label: 'Patient context received', done: ctxPresent && patient },
    { label: 'Ready to create referral packet', done: ready },
  ];
}

/**
 * Derive CTA state from the projection's next-action hint.
 * @returns {{ canProceed: boolean, label: string, mode: 'CONTINUE'|'INBOX'|'BLOCKED' }}
 */
export function getContinuationCtaState(projection) {
  const continueLabel = 'Create packet and import EHR candidates';
  const na = projection && projection.nextAction;
  if (na === CONTINUATION_NEXT_ACTION.OPEN_PACKET_INBOX) {
    return { canProceed: true, label: 'Open Packet Inbox', mode: 'INBOX' };
  }
  if (na === CONTINUATION_NEXT_ACTION.IMPORT || na === CONTINUATION_NEXT_ACTION.CREATE_AND_IMPORT) {
    return { canProceed: true, label: continueLabel, mode: 'CONTINUE' };
  }
  // RELAUNCH / BLOCKED / unknown → no forward action.
  return { canProceed: false, label: continueLabel, mode: 'BLOCKED' };
}

// Typed callable error code → safe UI copy (approved Build 2L copy set). Codes
// originate from smartLaunchPacket / smartLaunchImportMaterial / ehrUserMapping
// and travel in HttpsError.details.code.
const CODE_MESSAGE = Object.freeze({
  NOT_FOUND_LAUNCH_SESSION: 'Launch session not found.',
  FAILED_PRECONDITION_SESSION_EXPIRED: 'This EHR launch has expired. Please launch again from the EHR.',
  FAILED_PRECONDITION_SESSION_NOT_READY: 'This EHR launch is not ready yet. Please launch again from the EHR.',
  FAILED_PRECONDITION_NO_PATIENT_CONTEXT: 'Patient context was not received.',
  FAILED_PRECONDITION_CONTEXT_REJECTED: 'Patient context was not received.',
  FAILED_PRECONDITION_CONNECTION_DISABLED: 'This EHR connection is not active. Contact your administrator.',
  ALREADY_LINKED_PACKET_INVALID: 'This EHR launch has already been linked to a packet.',
  FAILED_PRECONDITION_PACKET_INVALID: 'This EHR launch has already been linked to a packet.',
  FAILED_PRECONDITION_IMPORT_MATERIAL_MISSING: 'EHR import material is missing. Please launch again.',
  FAILED_PRECONDITION_IMPORT_MATERIAL_EXPIRED: 'EHR import material expired. Please launch again.',
  FAILED_PRECONDITION_IMPORT_MATERIAL_CONSUMED: 'This EHR launch has already been imported.',
  FAILED_PRECONDITION_IMPORT_MATERIAL_INVALID: 'EHR import material is missing. Please launch again.',
  PERMISSION_DENIED_ROLE: 'Only a referring provider can continue this EHR launch.',
  PERMISSION_DENIED_TENANT: 'This EHR launch belongs to another tenant.',
  // Build 2I — EHR user↔GP mapping (only when the connection opts in).
  PERMISSION_DENIED_EHR_USER_MISMATCH: 'This EHR account is not linked to your ReferralGate account.',
  FAILED_PRECONDITION_FHIR_USER_MISSING: 'This EHR account is not linked to your ReferralGate account.',
  FAILED_PRECONDITION_USER_MAPPING_REQUIRED: 'This EHR account is not linked to your ReferralGate account.',
  FAILED_PRECONDITION_USER_MAPPING_NOT_FOUND: 'This EHR account is not linked to your ReferralGate account.',
  FAILED_PRECONDITION_USER_MAPPING_DISABLED: 'This EHR account is not linked to your ReferralGate account.',
});

const GENERIC_MESSAGE = 'This EHR launch could not be continued. Please launch again from the EHR.';

/** Extract the app-level typed code from a Functions HttpsError (or bare error). */
export function extractErrorCode(error) {
  if (!error) return null;
  if (error.details && typeof error.details.code === 'string') return error.details.code;
  if (typeof error.code === 'string') return error.code;
  return null;
}

/** Map a callable error to safe UI copy. Never returns a stack trace. */
export function mapContinuationError(error) {
  const code = extractErrorCode(error);
  if (code && CODE_MESSAGE[code]) return CODE_MESSAGE[code];
  return GENERIC_MESSAGE;
}

export default {
  LAUNCH_SESSION_ID_RE,
  isValidLaunchSessionId,
  parseLaunchSessionIdFromSearch,
  CONTINUATION_NEXT_ACTION,
  getContinuationRoleState,
  getContinuationStatusLines,
  getContinuationCtaState,
  extractErrorCode,
  mapContinuationError,
};
